import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-test-result-table>` says about itself.
 *
 * A test with NO outcome is not a pass. A run that was aborted, or is still going, leaves rows whose
 * result is not yet known, and they read "not run" and are counted separately. A screen that folded
 * them into the passes would tell somebody their suite is green when a third of it never executed,
 * which is the exact failure this project's first rule exists to prevent.
 *
 * Failures lead by default, and that is not a sort preference. A run of four hundred tests with six
 * failures is read for the six; ordering by class name means scrolling for them and ordering by
 * duration buries a fast failure under slow passes. Every column is still sortable — duration is how
 * somebody finds what is making the suite slow.
 *
 * The stack trace opens IN PLACE. Reading six failures usually means noticing what three of them
 * have in common, which a modal makes impossible.
 */
const docs: ComponentDocs = {
  aliases: ['test results', 'run results', 'failures list', 'test report', 'unit test results'],

  answers: [
    'see what failed in a test run',
    'read a failure’s message and stack trace',
    'find out which tests are making the suite slow',
    'see how much of a run actually executed',
  ],

  placements: ['appPage'],

  whenToUse: 'After a run, when the question is what it did — and almost always, specifically, what failed.',

  insteadUse: [
    { name: 'TestRunGrid', when: 'somebody is choosing what to run rather than reading what ran.' },
    { name: 'CoverageList', when: 'the question is coverage rather than pass or fail.' },
    {
      name: 'DataTable',
      when:
        'the rows are not test results. This one knows what a failure is, and orders and counts on ' +
        'that basis.',
    },
  ],

  inputs: [
    {
      name: 'results',
      type: 'json',
      description:
        'What each test did: `{ className, method, outcome?, durationMs?, message?, stack? }[]`. ' +
        '`outcome` ABSENT means NOT RUN — aborted, or still going — and is never counted as a pass. ' +
        'Setting this closes any open stack traces, because they belonged to the previous run.',
      default: [],
    },
    {
      name: 'sortKey',
      attribute: 'sort-key',
      type: 'text',
      options: ['outcome', 'className', 'method', 'durationMs'],
      description:
        'Which column orders the rows. Unset means failures first, then not-run, then skipped, then ' +
        'passes — the order a run is actually read in.',
      default: 'outcome',
    },
    {
      name: 'sortDirection',
      attribute: 'sort-direction',
      type: 'text',
      options: ['asc', 'desc'],
      description:
        'Which way. A test with no duration sorts last in BOTH directions rather than reading as the ' +
        'fastest test in the run.',
      default: 'asc',
    },
    {
      name: 'emptyText',
      attribute: 'empty-text',
      type: 'text',
      description: 'What to say before anything has run.',
      default: 'No results yet.',
    },
  ],

  state: [
    {
      name: 'tally',
      type: '{ pass, fail, skip, notRun }',
      description:
        'How many of each. `notRun` is its own number and is never added to any of the others — read ' +
        'it to gate anything that depends on a run having actually completed.',
    },
    {
      name: 'failures',
      type: 'CaosTestResult[]',
      description: 'The failures, in the order shown. Read it to build a re-run list without re-deciding what failed.',
    },
    { name: 'ordered', type: 'CaosTestResult[]', description: 'The results in the order they are drawn.' },
  ],

  parts: [
    { part: 'results', is: 'The outer column.' },
    { part: 'summary', is: 'The counts line.' },
    { part: 'table', is: 'The composed data table.' },
    { part: 'outcome', is: 'The result pill in a row.' },
    { part: 'detail', is: 'The detail cell.' },
    { part: 'message', is: 'The failure message.', when: 'The result carries a message or a stack.' },
    { part: 'expand', is: 'The show/hide stack-trace control.', when: 'The result carries a stack trace.' },
    { part: 'stack', is: 'The stack trace itself.', when: 'It is open.' },
  ],

  accessibility:
    'Every outcome is a WORD in its own pill — "failed", "passed", "skipped", "not run" — so colour ' +
    'repeats the result and never carries it. The counts line is a `role="status"` live region, so a ' +
    'finished run announces what it found rather than only redrawing. The stack-trace control is a ' +
    'real button with `aria-expanded` and an accessible name naming the test it belongs to, which ' +
    'matters on a screen holding forty identical-looking toggles.',

  examples: [
    {
      key: 'failures-first',
      label: 'A run with failures',
      summary:
        'The two failures lead, whatever order they arrived in, with the message beside each and the ' +
        'stack trace one press away in the row itself.',
      placement: {
        component: 'caos-test-result-table',
        inputs: {
          results: [
            { className: 'BillingServiceTest', method: 'chargesTax', outcome: 'pass', durationMs: 84 },
            {
              className: 'BillingServiceTest',
              method: 'roundsHalfUp',
              outcome: 'fail',
              durationMs: 91,
              message: 'expected 4.25 but got 4.24',
              stack: 'BillingService.round line 48\nBillingServiceTest.roundsHalfUp line 12',
            },
            { className: 'AccountTriggerTest', method: 'insertsHandler', outcome: 'pass', durationMs: 1220 },
            {
              className: 'QuoteCalculatorTest',
              method: 'clampsToFloor',
              outcome: 'fail',
              durationMs: 12,
              message: 'the floor was not applied',
              stack: 'QuoteCalculator.apply line 96',
            },
            { className: 'QuoteCalculatorTest', method: 'appliesDiscount', outcome: 'skip' },
          ],
        },
      },
      stage: { needsWidth: 860 },
    },
    {
      key: 'aborted',
      label: 'A run that was stopped part way',
      summary:
        'The tests that never executed read "not run" and are counted on their own. Folding them ' +
        'into the passes would report a suite as green when a third of it never ran.',
      placement: {
        component: 'caos-test-result-table',
        inputs: {
          results: [
            { className: 'BillingServiceTest', method: 'chargesTax', outcome: 'pass', durationMs: 84 },
            { className: 'BillingServiceTest', method: 'roundsHalfUp' },
            { className: 'BillingServiceTest', method: 'splitsByLine' },
            { className: 'AccountTriggerTest', method: 'insertsHandler' },
          ],
        },
      },
      stage: { needsWidth: 860 },
    },
    {
      key: 'by-duration',
      label: 'Ordered by what is slow',
      summary:
        'The other question this table answers. A test that was never timed sorts to the END in both ' +
        'directions rather than reading as the fastest in the run.',
      placement: {
        component: 'caos-test-result-table',
        inputs: {
          'sort-key': 'durationMs',
          'sort-direction': 'desc',
          results: [
            { className: 'AccountTriggerTest', method: 'insertsHandler', outcome: 'pass', durationMs: 1220 },
            { className: 'BillingServiceTest', method: 'chargesTax', outcome: 'pass', durationMs: 84 },
            { className: 'QuoteCalculatorTest', method: 'clampsToFloor', outcome: 'pass', durationMs: 12 },
            { className: 'QuoteCalculatorTest', method: 'appliesDiscount', outcome: 'skip' },
          ],
        },
      },
      stage: { needsWidth: 860 },
    },
  ],
};

export default docs;
